import type { ReactNode } from 'react';

import {
  DEFAULT_DOCUMENT_TITLE,
  documentTitleFromMarkdown,
  type DocumentDraft,
} from '@/entities/document-draft';
import {
  DocumentEditorWorkspace,
  type DocumentEditorWorkspaceProps,
} from '@/shared/ui/document-editor';

import type { useDocumentAttachments } from '../model/useDocumentAttachments';
import type { UseDocumentDraftResult } from '../model/useDocumentDraft';

type ConnectedProps =
  | 'title'
  | 'subtitle'
  | 'saved'
  | 'markdown'
  | 'onMarkdownChange'
  | 'attachments'
  | 'onUploadAttachment'
  | 'onRemoveAttachment'
  | 'uploadingAttachment'
  | 'attachmentError'
  | 'children';

interface DocumentDraftEditorProps extends Omit<DocumentEditorWorkspaceProps, ConnectedProps> {
  /** Lowercase noun used in status messages, e.g. "fax". */
  documentLabel: string;
  /** Title shown when the document has no `#` heading. */
  defaultTitle?: string;
  editor: UseDocumentDraftResult;
  attachments: ReturnType<typeof useDocumentAttachments>;
  renderDialogs?(draft: DocumentDraft): ReactNode;
}

function saveLabel(editor: UseDocumentDraftResult): string {
  if (editor.saveState === 'saving') {
    return 'Saving…';
  }
  if (editor.saveState === 'error') {
    return 'Save failed';
  }
  if (editor.saveState === 'idle' && editor.isNewDraft) {
    return 'Not saved';
  }
  return 'Saved';
}

/** Connects a loaded draft to the shared editor and handles its load states. */
export function DocumentDraftEditor({
  documentLabel,
  defaultTitle = DEFAULT_DOCUMENT_TITLE,
  editor,
  attachments,
  renderDialogs,
  sendDisabled = false,
  ...workspaceProps
}: DocumentDraftEditorProps) {
  if (editor.loadState === 'loading') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading {documentLabel} editor…
      </div>
    );
  }
  if (editor.loadState === 'error') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        Unable to load {documentLabel} editor data. Please try again.
      </div>
    );
  }
  const { draft } = editor;
  if (editor.loadState === 'missing' || draft === null) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        This {documentLabel} draft no longer exists.
      </div>
    );
  }

  return (
    <DocumentEditorWorkspace
      {...workspaceProps}
      title={documentTitleFromMarkdown(draft.content.bodyMarkdown, defaultTitle)}
      subtitle={`Draft · ${saveLabel(editor)} · Last edited by ${
        draft.modifiedBy.displayName || 'Admin'
      }`}
      saved={editor.saveState === 'saved'}
      sendDisabled={sendDisabled || editor.saveState === 'saving'}
      markdown={draft.content.bodyMarkdown}
      onMarkdownChange={editor.updateDocumentMarkdown}
      attachments={draft.content.attachments}
      onUploadAttachment={attachments.upload}
      onRemoveAttachment={attachments.remove}
      uploadingAttachment={attachments.uploading}
      attachmentError={attachments.error}
    >
      {renderDialogs?.(draft)}
    </DocumentEditorWorkspace>
  );
}
